import { Alert, Button, Checkbox, Form, Input, InputNumber, Modal, Select, Tag, TreeSelect, Upload } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import * as React from 'react';
import { useEffect, useRef, useState, useTransition } from 'react';
import { useHistory } from 'react-router';
import dspApi from './DspApi';
import { DspBean, TimeZoneBean, CountryBean, ClusterKeyValue, DictionaryOption, IabCategoryBean } from '../../../api/api';
import { formDataLoader, FormDataType } from '../../../util/FormDataLoader';
import { FEE_CONFIG_DSP, getIabCategoriesAsTree, resolveIabDefaultsChange, truncateErrorMessage } from '../../../util/Util';
import { StorageKey, storageWriterFactory } from '../../../util/Storage';
import DspTabs, { DspTabKey, dspTabs } from './DspTabs';
import { BasicDetails, FilteringOptions } from './DspTabSections';
import DomainListCard from './DomainListCard';

const FormItem = Form.Item;
const Option = Select.Option

interface Props {
    miniExchange?: boolean
}

const formInfoItems: FormDataType[] = [
    FormDataType.TimeZones,
    FormDataType.Countries,
    FormDataType.ClusterEndpoints,
    FormDataType.TargetDeviceTypes,
    FormDataType.TargetOperatingSystems,
    FormDataType.IabCategories
]

const WinNotificationEnum = DspBean.WinNotificationEnum

const DspCreate: React.FC<Props> = ({ miniExchange }) => {

    const [info, setInfo] = useState<{
        timezones: TimeZoneBean[];
        countries: CountryBean[];
        targetDeviceTypes: DictionaryOption[];
        targetOperatingSystems: DictionaryOption[];
        iabCategories: IabCategoryBean[];
    }>({
        timezones: [],
        countries: [],
        targetDeviceTypes: [],
        targetOperatingSystems: [],
        iabCategories: [],
    })

    const [form] = useForm()
    const [error, setError] = useState()
    const history = useHistory()
    const [validation, setValidation] = useState(undefined);
    const storageWriter = storageWriterFactory(miniExchange ? 'miniExchange' : 'dsp')

    const [activeTab, setActiveTab] = useState<DspTabKey>('basic')
    const [environmentType, setEnvironmentType] = useState<string[]>(['APP', 'WEB'])
    const [environmentError, setEnvironmentError] = useState('')
    const [clusters, setClusters] = useState<ClusterKeyValue[]>([])
    const [defaultCluster, setDefaultCluster] = useState<string>('')
    const [clusterUrlValues, setClusterUrlValues] = useState<Record<string, string>>({})

    // ─── Domains state ──────────────────────────────────────────────────────
    const [domains, setDomains] = useState<string[]>([])
    const [acceptAllDomains, setAcceptAllDomains] = useState(false)
    const [blackListDomains, setBlackListDomains] = useState(false)
    const [domainsCollapsed, setDomainsCollapsed] = useState(false)

    // ─── Mobile App IDs state ───────────────────────────────────────────────
    const [mobileAppIds, setMobileAppIds] = useState<string[]>([])
    const [acceptAllMobileAppIds, setAcceptAllMobileAppIds] = useState(false)
    const [blackListMobileAppIds, setBlackListMobileAppIds] = useState(false)
    const [mobileAppsCollapsed, setMobileAppsCollapsed] = useState(false)

    const iabPrevRef = useRef<number[]>([])

    const handleClusterUrlChange = (key: string, value: string) => {
        const updated = { ...clusterUrlValues, [key]: value }
        setClusterUrlValues(updated)
        if (!defaultCluster && value) {
            setDefaultCluster(key)
        } else if (defaultCluster === key && !value) {
            const nextFilled = clusters.find(c => c.key !== key && updated[c.key])
            setDefaultCluster(nextFilled?.key ?? '')
        }
    }

    const loadData = async () => {
        try {
            let formInfo = await formDataLoader(...formInfoItems)
            setInfo(formInfo as any)
            if (Array.isArray(formInfo.clusterEndpoints) && formInfo.clusterEndpoints.length > 0) {
                setClusters(formInfo.clusterEndpoints)
            } else {
                setError('Failed to load cluster endpoints. Please refresh the page.' as any)
            }
        } catch (e) {
            onRejected(e)
        }
    }

    useEffect(() => {
        loadData()
    }, [])

    const onRejected = err => {
        setError(truncateErrorMessage(err?.error))
    }

    const handleTargetCountrySelect = (value: string) => {
        const allIso3 = (info.countries ?? [])
            .map((c) => c.iso3)
            .filter((v): v is string => Boolean(v))

        if (value === 'All') {
            form.setFieldsValue({ targetCountries: ['All', ...allIso3] })
        } else {
            const current: string[] = form.getFieldValue('targetCountries') ?? []
            const selectedCountries = current.filter((v) => v !== 'All')
            if (selectedCountries.length === allIso3.length) {
                form.setFieldsValue({ targetCountries: ['All', ...selectedCountries] })
            }
        }
    }

    const handleTargetCountryDeselect = (value: string) => {
        if (value === 'All') {
            form.setFieldsValue({ targetCountries: [] })
        } else {
            const current: string[] = form.getFieldValue('targetCountries') ?? []
            form.setFieldsValue({ targetCountries: current.filter((v) => v !== 'All' && v !== value) })
        }
    }

    const handleAllOptionSelect = (fieldName: string, allOptions: string[]) => (value: string) => {
        if (value === 'All') {
            form.setFieldsValue({ [fieldName]: ['All', ...allOptions] })
        } else {
            const current: string[] = form.getFieldValue(fieldName) ?? []
            const selected = current.filter((v) => v !== 'All')
            if (selected.length === allOptions.length) {
                form.setFieldsValue({ [fieldName]: ['All', ...selected] })
            }
        }
    }

    const handleAllOptionDeselect = (fieldName: string) => (value: string) => {
        if (value === 'All') {
            form.setFieldsValue({ [fieldName]: [] })
        } else {
            const current: string[] = form.getFieldValue(fieldName) ?? []
            form.setFieldsValue({ [fieldName]: current.filter((v) => v !== 'All' && v !== value) })
        }
    }

    const handleSubmit = () => {
        if (!form.getFieldValue('validation')) {
            form.setFieldsValue({ validation: false })
        }
        if (environmentType.length === 0) {
            setEnvironmentError('Please select at least one environment')
            setActiveTab('filtering')
            return
        }
        setEnvironmentError('')
        form.validateFields().then(values => {
            const targetCountries: string[] = values?.targetCountries ?? []
            const deviceTargeting: string[] = values?.targetDeviceTypes ?? []
            const osTargeting: string[] = values?.targetOperatingSystems ?? []
            const environment = environmentType.length === 2 ? 'BOTH' : (environmentType[0] ?? 'BOTH')
            const clusterEndpoints = clusters
                .filter(c => values[`cluster_${c.key}`])
                .map(c => ({
                    cluster: c.key,
                    endpointUrl: values[`cluster_${c.key}`],
                    defaultEndpoint: defaultCluster === c.key,
                }))
            const selectedIabIds: number[] = values?.iabCategoryIds ?? []
            const codeByIabId = new Map(info.iabCategories.map(c => [c.id, c.code]))
            const allowedIabCategoryCodes = selectedIabIds
                .map(id => codeByIabId.get(id))
                .filter((code): code is string => Boolean(code))
            const payload = {
                ...values,
                clusterEndpoints,
                targetCountries: targetCountries.includes('All') ? ['All'] : targetCountries,
                targetDeviceTypes: deviceTargeting.includes('All') ? ['All'] : deviceTargeting,
                targetOperatingSystems: osTargeting.includes('All') ? ['All'] : osTargeting,
                domains: acceptAllDomains ? [] : domains,
                acceptAllDomains,
                blackListDomains,
                mobileAppIds: acceptAllMobileAppIds ? [] : mobileAppIds,
                acceptAllMobileAppIds,
                blackListMobileAppIds,
                environment,
                allowedIabCategoryCodes,
            };
            payload.fee = payload.dspFee;
            delete payload.dspFee;
            delete payload.iabCategoryIds;
            clusters.forEach(c => delete payload[`cluster_${c.key}`]);

            dspApi
                .create(payload)
                .then(bean => userCredentials(bean.data?.authDataBean))
                .catch(err => {
                    setError(truncateErrorMessage(err?.error))
                })
        }).catch(() => {
            setActiveTab('basic')
        });
    };

    const onChangeIab = (value: number[]) => {
        const next = resolveIabDefaultsChange(value || [], iabPrevRef.current, info.iabCategories as any)
        iabPrevRef.current = next
        form.setFieldsValue({ iabCategoryIds: next })
        return next
    }

    const navigateToLastPage = async () => {
        const listPath = miniExchange ? '/mini-exchange' : '/dsp';
        try {
            const response = await dspApi.list(miniExchange, 1, 1);
            const total = response.pagination?.total || 1;
            const pageSize = 10;
            const lastPage = Math.ceil(total / pageSize);
            
            storageWriter.write(StorageKey.SearchParams, {
                page: lastPage,
            });

            history.push(listPath, { fromDspList: true });
        } catch {
            history.push(listPath, { fromDspList: true });
        }
    };

    const userCredentials = (authDataBean) => {
        Modal.info({
            title: 'New account created',
            content: <div>
                <br />
                <div>Please save account credentials</div>
                <br />
                <div>Username: {authDataBean.username}</div>
                <div>Password: {authDataBean.password}</div>
            </div>,
            okText: 'OK',
            okCancel: false,
            onOk: () => navigateToLastPage(),
        });
    };

    const handleValidation = (value) => {
        setValidation(value.target.checked)
    };

    // ─── Domain handlers ────────────────────────────────────────────────────
    const handleAddDomain = (value: string) => setDomains(prev => [...prev, value])

    const handleUploadDomains = (file: File) => {
        const reader = new FileReader()
        reader.onloadend = () => {
            const lines = (reader.result as string).split('\n').map(l => l.trim()).filter(Boolean)
            setDomains(prev => {
                const existingLower = new Set(prev.map(d => d.toLowerCase()))
                return [...prev, ...lines.filter(d => !existingLower.has(d.toLowerCase()))]
            })
        }
        reader.readAsText(file)
    }

    const handleRemoveDomain = (index: number) =>
        setDomains(prev => prev.filter((_, i) => i !== index))

    const handleClearAllDomains = () => {
        Modal.confirm({
            title: 'Clear all domains?',
            content: 'This cannot be undone.',
            okText: 'Clear All',
            okButtonProps: { danger: true },
            cancelText: 'Cancel',
            onOk: () => setDomains([]),
        })
    }

    // ─── Mobile App ID handlers ─────────────────────────────────────────────
    const handleAddMobileAppId = (value: string) => setMobileAppIds(prev => [...prev, value])

    const handleUploadMobileAppIds = (file: File) => {
        const reader = new FileReader()
        reader.onloadend = () => {
            const lines = (reader.result as string).split('\n').map(l => l.trim()).filter(Boolean)
            setMobileAppIds(prev => {
                const existingLower = new Set(prev.map(d => d.toLowerCase()))
                return [...prev, ...lines.filter(d => !existingLower.has(d.toLowerCase()))]
            })
        }
        reader.readAsText(file)
    }

    const handleRemoveMobileAppId = (index: number) =>
        setMobileAppIds(prev => prev.filter((_, i) => i !== index))

    const handleClearAllMobileAppIds = () => {
        Modal.confirm({
            title: 'Clear all mobile app IDs?',
            content: 'This cannot be undone.',
            okText: 'Clear All',
            okButtonProps: { danger: true },
            cancelText: 'Cancel',
            onOk: () => setMobileAppIds([]),
        })
    }

    return (
        <div>
            {/* ── Sticky header ── */}
            <div style={{
                position: 'sticky', top: 0, zIndex: 10,
                background: '#fff', border: '1px solid #f0f0f0',
                borderRadius: 8, padding: 12, marginBottom: 16,
                display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', gap: 12,
            }}>
                <Button
                    type="default"
                    htmlType="button"
                    onClick={() => history.push(miniExchange ? '/mini-exchange' : '/dsp', { fromDspList: true })}
                >
                    ← Go Back
                </Button>
                <DspTabs
                    tabs={dspTabs}
                    activeTab={activeTab}
                    onTabChange={(value) => setActiveTab(value)}
                />
                <div style={{ display: 'flex', gap: 8 }}>
                    <Button type="default" htmlType="button" onClick={() => history.push(miniExchange ? '/mini-exchange' : '/dsp', { fromDspList: true })}>Cancel</Button>
                    <Button type="primary" htmlType="button" onClick={handleSubmit}>Create</Button>
                </div>
            </div>

        <Form
            style={{
                maxWidth: '1000px'
            }}
            onFinish={handleSubmit}
            form={form}
            labelCol={{ span: 7 }}
            wrapperCol={{ span: 17 }}
        >
            {error ? <Alert message={error} type="error" /> : null}

            <BasicDetails active={activeTab === 'basic'}>
            <FormItem
                name="name"
                rules={[
                    {
                        required: true,
                        message: 'Please input DSP name'
                    },
                    {
                        max: 255,
                        message: 'Name cannot be longer that 255 characters'
                    }
                ]}
                label="Name"
                tooltip={'Name of the dsp'}
            >
                <Input />
            </FormItem>
            <FormItem
                label="Cluster Endpoints"
                required
                tooltip={'DSP endpoint URL per cluster region'}
                style={{ alignItems: 'flex-start' }}
                labelCol={{ span: 7, style: { paddingTop: '2px' } }}
            >
                <div>
                    {clusters.map((cluster: ClusterKeyValue, idx: number) => (
                        <div key={cluster.key}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '0px 0', width: '100%' ,paddingTop: '-2px'}}>
                                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                    <span style={{ width: 110, flexShrink: 0, fontSize: 13, color: 'rgba(0,0,0,0.85)' }}>
                                        {cluster.value}
                                    </span>
                                    <FormItem
                                        name={`cluster_${cluster.key}`}
                                        noStyle
                                        rules={[
                                            ...(defaultCluster === cluster.key
                                                ? [{ required: true, message: 'Please input the endpoint URL for the default cluster' }]
                                                : []),
                                            { max: 255, message: 'URL cannot be longer than 255 characters' },
                                        ]}
                                    >
                                        <Input
                                            style={{ flex: 1 }}
                                            onChange={e => handleClusterUrlChange(cluster.key, e.target.value)}
                                        />
                                    </FormItem>
                                    <Button
                                        type="default"
                                        disabled={!clusterUrlValues[cluster.key]}
                                        onClick={() => setDefaultCluster(cluster.key)}
                                        style={{
                                            flexShrink: 0,
                                            minWidth: 106,
                                            height: 36,
                                            borderRadius: 8,
                                            padding: '0 10px',
                                            ...(defaultCluster === cluster.key && clusterUrlValues[cluster.key] ? {
                                                backgroundColor: '#EBF3FF',
                                                borderColor: '#1A73E8',
                                                color: '#1A73E8',
                                            } : {}),
                                        }}
                                    >
                                        {defaultCluster === cluster.key && clusterUrlValues[cluster.key] ? 'Default' : 'Set Default'}
                                    </Button>
                                </div>
                                <div style={{
                                    color: '#1A73E8',
                                    fontFamily: 'Inter, sans-serif',
                                    fontWeight: 400,
                                    fontSize: 12,
                                    lineHeight: '20px',
                                    letterSpacing: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    height: 20,
                                    visibility: defaultCluster === cluster.key && clusterUrlValues[cluster.key] ? 'visible' : 'hidden',
                                }}>
                                    This endpoint applies as the default across all regions
                                </div>
                            </div>
                            {idx < clusters.length - 1 && (
                                <div style={{ borderTop: '1px solid #f0f0f0', margin: '4px 0' }} />
                            )}
                        </div>
                    ))}
                </div>
            </FormItem>
            <FormItem
                name="dspType"
                label="DSP Type"
                rules={[{ required: true, message: 'Please select DSP Type' }]}
            >
                <Select placeholder="Select DSP Type">
                    {Object
                        .keys(DspBean.DspTypeEnum)
                        .map((type) =>
                            <Option key={type} value={type}>{type}</Option>
                        )}
                </Select>
            </FormItem>
            <FormItem
                name="tmax"
                rules={[
                    {
                        required: true,
                        message: 'Please input Timeout'
                    },
                    {
                        type: 'integer',
                        min: 1,
                        max: 10000,
                        message: 'Please input a valid number',
                    }
                ]}

                label="Timeout"
                tooltip={'Maximum wait time for the answer from dsp in milliseconds 1-10000'}
            >
                <InputNumber min={1} max={10000} />
            </FormItem>
            <FormItem
                name="maxQps"
                label="QPS"
                tooltip={'Maximum queries per second allowed'}
            >
                <InputNumber min={1} max={10000} />
            </FormItem>
            <FormItem
                name="gzipCompression"
                label="Gzip compression"
                valuePropName="checked"
                initialValue={true}
                tooltip={'If true than gzip compression will be used'}
            >
                <Checkbox />
            </FormItem>
            <FormItem
                name="miniExchange"
                label="Mini Exchange"
                valuePropName="checked"
                initialValue={miniExchange}
                hidden
                tooltip={'If true than it will be considered as Mini Exchange'}
            >
                <Checkbox />
            </FormItem>
            <FormItem
             required
             label="DSP Fee"
             tooltip="DSP Fee by Media Type"
             >
                <div style={{ display: 'flex', gap: '20px',paddingTop: '4px' }}>
                    {FEE_CONFIG_DSP.map((item, index) => (
                        <div key={item.mediaType} style={{ display: 'flex', flexDirection: 'column',flex:1 }}>
                            <div style={{ marginBottom: '4px', fontSize: '14px', color: 'rgba(0, 0, 0, 0.85)' }}>
                                {item.mediaType !== 'CTV' && <span style={{ color: '#ff4d4f' }}>* </span>}{item.label}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                                <FormItem
                                    name={['dspFee', index, 'fee']}
                                    initialValue={0}
                                    rules={item.mediaType !== 'CTV' ? [{ required: true, message: `${item.label} fee is required` }] : []}
                                    style={{ margin: 0 }}
                                    validateTrigger={['onChange', 'onBlur', 'onSubmit']}
                                >
                                    <InputNumber step={0.01} min={0} max={99} style={{ width: '100%' }} />
                                </FormItem>
                                <span style={{ lineHeight: '32px' }}>%</span>
                                <FormItem
                                    name={['dspFee', index, 'mediaType']}
                                    initialValue={item.mediaType}
                                    hidden
                                    style={{ margin: 0 }}
                                >
                                    <Input />
                                </FormItem>
                            </div>
                        </div>
                    ))}
                </div>
            </FormItem>
            <FormItem
                name="winNotification"
                label="Win notification"
            >
                <Select
                    allowClear
                    mode="multiple"
                    placeholder="Select win notification type">
                    {Object.values(WinNotificationEnum).map(win =>
                        <Option key={win} value={win}>{win}</Option>)}
                </Select>
            </FormItem>
            <FormItem
                name="userSyncEndpoint"
                rules={[
                    {
                        max: 255,
                        message: 'Url cannot be longer that 255 characters'
                    }
                ]}
                label="Cookie sync URL"
                tooltip={'DSP cookie sync endpoint URL'}
            >
                <Input />
            </FormItem>
            <FormItem
                name="userSyncUidMacro"
                rules={[
                    {
                        max: 255,
                        message: 'Macro cannot be longer that 255 characters'
                    }
                ]}
                label="Cookie sync UID macro"
                tooltip={'DSP cookie sync user ID macro'}
            >
                <Input />
            </FormItem>
            <FormItem
                name="validation"
                label="Validation"
                valuePropName="checked"
                tooltip={'If true dsp should send signed response only'}
            >
                <Checkbox onChange={handleValidation} />
            </FormItem>
            <FormItem
                name="publicValidationKey"
                label="Public validation key"
                rules={[
                    {
                        max: 255,
                        message: 'Public validation key cannot be longer that 255 characters'
                    }
                ]}
                tooltip={'Public validation key need to validate dsp response'}
            >
                <Input disabled={!validation} />
            </FormItem>
            <FormItem
                name="active"
                label="Active"
                valuePropName="checked"
                initialValue={true}
                tooltip={'If true than the requests will be sent to dsp'}
            >
                <Checkbox />
            </FormItem>
            <FormItem
                name="email"
                rules={[
                    { required: true, message: 'Please input Dsp email' },
                    {
                        pattern: new RegExp('.+@.+\\..+'),
                        message: 'Email is incorrect'
                    }
                ]}
                label="Email"
            >
                <Input />
            </FormItem>
            <FormItem
                name="timezoneId"
                rules={[{ required: true, message: 'Please select Time Zone', type: 'string' }]}
                label="Time Zone"
            >
                <Select
                    showSearch
                    placeholder="Select time zone">
                    {info.timezones.map((timezone: TimeZoneBean) =>
                        <Option key={timezone.id} value={timezone.id as string}>{timezone.id}</Option>)}
                </Select>
            </FormItem>
            <FormItem
                required
                label="Validations limit"
                tooltip="Limit the number of validation report per month"
            >
                <FormItem
                    name="athenaValidationsLimit"
                    rules={[{ required: true, message: 'Limit is required' }]}
                    className="ant-item ant-item-first"
                >
                    <InputNumber min={0} defaultValue={0} />
                </FormItem>
            </FormItem>
            <FormItem
                required
                label="Request/Response Id period limit"
                tooltip="Limit of period for Request/Response Id report in hours per month"
            >
                <FormItem
                    name="athenaPeriodLimit"
                    rules={[{ required: true, message: 'Limit is required' }]}
                    className="ant-item ant-item-first"
                >
                    <InputNumber min={0} defaultValue={0} />
                </FormItem>
            </FormItem>
            </BasicDetails>
        </Form>

        {/* ── FILTERING OPTIONS TAB — accordions outside Form ── */}
        <FilteringOptions active={activeTab === 'filtering'}>
            <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
                <DomainListCard
                    title="Web Domains"
                    subtitle="Manage allowlist & blocklist entries"
                    items={domains}
                    acceptAll={acceptAllDomains}
                    blackList={blackListDomains}
                    collapsed={domainsCollapsed}
                    onToggleCollapse={() => setDomainsCollapsed(v => !v)}
                    onAcceptAllChange={checked => { setAcceptAllDomains(checked); if (checked) setDomains([]) }}
                    onBlackListChange={setBlackListDomains}
                    onAdd={handleAddDomain}
                    onUpload={handleUploadDomains}
                    onRemoveItem={handleRemoveDomain}
                    onClearAll={handleClearAllDomains}
                />
                <DomainListCard
                    title="MobileApp ID"
                    subtitle="Manage allowlist & blocklist entries"
                    items={mobileAppIds}
                    acceptAll={acceptAllMobileAppIds}
                    blackList={blackListMobileAppIds}
                    collapsed={mobileAppsCollapsed}
                    onToggleCollapse={() => setMobileAppsCollapsed(v => !v)}
                    onAcceptAllChange={checked => { setAcceptAllMobileAppIds(checked); if (checked) setMobileAppIds([]) }}
                    onBlackListChange={setBlackListMobileAppIds}
                    onAdd={handleAddMobileAppId}
                    onUpload={handleUploadMobileAppIds}
                    onRemoveItem={handleRemoveMobileAppId}
                    onClearAll={handleClearAllMobileAppIds}
                />
            </div>
            <Form style={{ maxWidth: '1000px' }} form={form} labelCol={{ span: 7 }} wrapperCol={{ span: 17 }}>
                <FormItem
                    label="Environment"
                    required
                    tooltip={'Select the environment(s) for the DSP'}
                    validateStatus={environmentError ? 'error' : undefined}
                    help={environmentError || undefined}
                >
                    <Checkbox.Group
                        value={environmentType}
                        onChange={(val: any) => {
                            setEnvironmentType(val)
                            if (val.length > 0) setEnvironmentError('')
                        }}
                        options={[
                            { label: 'Web', value: 'WEB' },
                            { label: 'App', value: 'APP' },
                        ]}
                    />
                </FormItem>
                <FormItem
                    name="targetCountries"
                    label="Countries"
                    tooltip="Geo countries for the DSP"
                >
                    <Select
                        mode="multiple"
                        showSearch
                        allowClear
                        placeholder="Select Countries"
                        optionFilterProp="children"
                        onSelect={handleTargetCountrySelect}
                        onDeselect={handleTargetCountryDeselect}
                        filterOption={(input, option) =>
                            (option?.children as unknown as string)?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                    >
                        <Option key="All" value="All">All</Option>
                        {info.countries.map((country: CountryBean) => (
                            <Option
                                key={country.iso3 ?? country.id}
                                title={country.name}
                                value={(country.iso3 ?? '') as string}
                                disabled={!country.iso3}
                            >
                                {country.name}
                            </Option>
                        ))}
                    </Select>
                </FormItem>
                <FormItem
                    name="targetDeviceTypes"
                    label="Device Targeting"
                    tooltip="Device types targeted by the DSP"
                >
                    <Select
                        mode="multiple"
                        allowClear
                        showSearch
                        placeholder="Select Device Targeting"
                        optionFilterProp="children"
                        onSelect={handleAllOptionSelect('targetDeviceTypes', info.targetDeviceTypes.map((o) => o.value))}
                        onDeselect={handleAllOptionDeselect('targetDeviceTypes')}
                        filterOption={(input, option) =>
                            (option?.children as unknown as string)?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                    >
                        <Option key="All" value="All">All</Option>
                        {info.targetDeviceTypes.map((opt) => (
                            <Option key={opt.value} value={opt.value}>{opt.name}</Option>
                        ))}
                    </Select>
                </FormItem>
                <FormItem
                    name="targetOperatingSystems"
                    label="OS Targeting"
                    tooltip="Operating systems targeted by the DSP"
                >
                    <Select
                        mode="multiple"
                        allowClear
                        showSearch
                        placeholder="Select OS Targeting"
                        optionFilterProp="children"
                        onSelect={handleAllOptionSelect('targetOperatingSystems', info.targetOperatingSystems.map((o) => o.value))}
                        onDeselect={handleAllOptionDeselect('targetOperatingSystems')}
                        filterOption={(input, option) =>
                            (option?.children as unknown as string)?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                    >
                        <Option key="All" value="All">All</Option>
                        {info.targetOperatingSystems.map((opt) => (
                            <Option key={opt.value} value={opt.value}>{opt.name}</Option>
                        ))}
                    </Select>
                </FormItem>
                <FormItem
                    name="iabCategoryIds"
                    label="IAB Categories"
                    tooltip={'IAB categories allowed for this DSP'}
                >
                    <TreeSelect
                        allowClear
                        treeCheckable
                        showCheckedStrategy="SHOW_ALL"
                        style={{ maxHeight: '400px', overflow: 'auto' }}
                        placement={'topRight'}
                        placeholder="Select IAB categories"
                        onChange={onChangeIab}
                        treeData={getIabCategoriesAsTree(info.iabCategories as any)}
                        treeNodeFilterProp="title"
                    />
                </FormItem>
            </Form>
        </FilteringOptions>
        </div>
    );
}

export default DspCreate;